import { useCallback, useEffect, useMemo, useState } from "react";

import log from "logbeacon";
import { pubsub } from "utils";
import { useSnapshot } from "valtio";
import { CommonConfirmModalCallable } from "../components/modal/CommonConfirmModal";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";
import Translations from "@translate/hooks/usePortfolioManage";
import { I18nUtil } from "@utils/i18n";
import { favoritesCenter } from "@/src/store/favorite/FavoritesCenter";
import { rootStore } from "@/src/store";
import { useLoginModalProvider } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export interface UsePortfolioManageParams {
  id: string;
  type?: "TOKEN" | "STOCK";
  portfolioId?: string;
}

export interface UsePortfolioManageReturn {
  isFavorite: boolean;
  isLoggedIn?: boolean;
  currentFavoriteStates: readonly string[];
  handleItemClick: () => void;
  handleStopPropagation: (e: React.MouseEvent) => void;
  isLoading: boolean;
}

/**
 * 投资组合管理逻辑的自定义 Hook
 * 处理收藏状态、登录状态以及相关的交互逻辑
 *
 * @param id - 当前操作对象的唯一标识（如 token id）
 * @param type - 资产类型 (TOKEN | STOCK)
 * @param isFavorite - 外部传入的收藏状态（可选）
 * @returns 投资组合管理相关的状态和处理函数
 */
export const usePortfolioManage = ({
  id,
  type,
}: UsePortfolioManageParams): UsePortfolioManageReturn => {
  // 获取登录状态
  const {
    isLoggedIn,
    isLoading: isLoginLoading,
    isFetching: isLoginFetching,
  } = useSnapshot(rootStore.login);
  const { open } = useLoginModalProvider();

  // 获取收藏状态数据 - 观察整个 state 对象
  const { allFavoriteData, userFolders } = useSnapshot(favoritesCenter.state);
  const folderId = userFolders?.[0]?.id || "";
  const [loading, setLoading] = useState(true);
  // 使用 useMemo 优化 currentFavoriteStates 的计算
  // 通过观察版本号确保响应式更新
  const currentFavoriteStates = useMemo(() => {
    // 访问版本号确保响应式更新
    const data = allFavoriteData?.data;
    // 从数据中获取当前 ID 的状态
    return data?.[id] || null;
  }, [allFavoriteData?.data, id]);

  // 确定最终的收藏状态
  const isFavorite = folderId
    ? currentFavoriteStates?.favoriteStates?.includes(folderId)
    : currentFavoriteStates?.favoriteStates?.length > 0;
  // 处理投资组合的勾选/取消勾选
  const handleItemClick = useCallback(async () => {
    if (!isLoggedIn) {
      open?.();
    }

    pubsub.emit(pubSubKeyMap.COLLECT_CHANGE);

    // 确定操作类型
    const action = !isFavorite ? "ADD" : "REMOVE";
    if (action === "REMOVE") {
      await CommonConfirmModalCallable({
        title: I18n.t("Remove from portfolio"),
        description: I18n.t(
          "Do you really want to remove this asset?All associated transactions will also be removed.",
        ),
        onConfirm: () => {
          favoritesCenter.updateFavoriteStatus(id, folderId, action, type);
        },
      });
      return;
    }
    // 调用 FavoritesCenter 更新收藏状态
    favoritesCenter.updateFavoriteStatus(id, folderId, action, type);
    log.debug("[usePortfolioManage] Portfolio selection updated:", {
      id,
      itemId: folderId,
      action,
    });
  }, [id, isLoggedIn, type, isFavorite, folderId]);

  useEffect(() => {
    if (isLoginLoading || isLoginFetching) {
      return;
    }
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    setLoading(allFavoriteData === undefined);
  }, [isLoggedIn, isLoginLoading, allFavoriteData, isLoginFetching]);

  // 阻止事件冒泡
  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return {
    isFavorite,
    isLoggedIn,
    currentFavoriteStates,
    handleItemClick,
    handleStopPropagation,
    isLoading: loading,
  };
};
